import AddIcon from "@mui/icons-material/Add";
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import EditIcon from "@mui/icons-material/Edit";
import { Box, Button, Card, CardHeader, Chip, Divider, Grid, IconButton, Stack, ToggleButton, ToggleButtonGroup, Tooltip, Typography } from "@mui/material";
import { push } from "connected-react-router";
import { ListContextProvider, useListContext } from "ra-core";
import { createContext, FC, useContext, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { useDispatch } from "react-redux";
import { Pagination as DefaultPagination } from '~/components/list/pagination';
import { Content } from "~/components/content/Content";
import { FunctionField, TextField } from "~/components/field";
import { Datagrid } from "~/components/list/datagrid";
import { AtopFuncArea, SearchField, FilterField, FilterOption } from "~/components/list/datagridFunctionWidgets";
import useLocalListController from "~/components/list/useLocalListController";
import Scrollbar from "~/components/Scrollbar";
import { EDIT_DATA_CLASSIFICATION_ENCRYPTION } from "~/constants";
import { RecPatch } from "~/dataProvider/tools";
import { DataInfoTypeResponseDto } from "~/gen/api/encrypt";
import { OdResourcePageProps } from "~/types/resource";
import { useOptionsData } from "~/components/settings/encryption/DatabaseClassificationProtection/DataInfoTypeEditor";
import ClassificationLabel from "~/components/governance/labels/ClassificationLabel";
import { SecondaryClassificationResponseDto } from "~/gen/api/governance";
import { useHasPermit } from "~/authProvider/useHasPermit";



const infoTypeListOptions : {sorters: Record<string, (a:DataInfoTypeResponseDto & RecPatch, b:DataInfoTypeResponseDto & RecPatch) => number>} = {
  sorters: {
    id: (a, b) => a.id < b.id ? -1 : 1,
    name: (a, b) => (a.name || '').toLowerCase() < (b.name || '').toLowerCase() ? -1 : 1,
  }
}

const DataInfoTypes: FC<OdResourcePageProps> = (props) => {
  const { resource } = props
  
  return (
    <ListContextProvider value={useLocalListController({ basePath: '' }, infoTypeListOptions)}>
      <DataInfoTypesBody {...props} />
    </ListContextProvider>
  )
}


const DataInfoTypesBody: FC<OdResourcePageProps> = ({
  options,
  resource,
}) => {
  const hasEditPermit = useHasPermit([EDIT_DATA_CLASSIFICATION_ENCRYPTION])
  const listCtx = useListContext()
  if (listCtx.currentSort.field !== 'name') {
    setTimeout(() => listCtx.setSort('name'))
  }
  const dispatch = useDispatch()

  const [states, setStates] = useState({ showColumn: 'C' as 'D'|'C' })
  const { classifications, ruleMap } = useOptionsData()
  const rules = Object.values(ruleMap).flatMap(x => x)

  return (
    // <Content pageName="Data Info Types" resourceName="Data Info Types" 
    //   breadcrumbs={[...(options?.breadcrumbs ?? [])]}
    //   mainActions={
    //     <Button
    //       color="primary"
    //       startIcon={<AddIcon fontSize="small" />}
    //       variant="contained"
    //       onClick={() => dispatch(push(`/${resource}/create`))}
    //     >
    //       Create Data Info Type
    //     </Button>
    //   }
    // >
    //   <Grid container spacing={4} sx={{ mt: 2 }} >
    //     <Grid item xs={12}>
          <Card>
            <CardHeader 
              title="Data Info Types"
              action={
                hasEditPermit &&
                  <Button
                    color="primary"
                    variant="contained"
                    onClick={() => dispatch(push(`/${resource}/create`))}
                  >
                    Create
                  </Button>
              }
            />
            <Divider />
            <AtopFuncArea >
              <SearchField 
                sourceKey="dbName"
                match={(d: DataInfoTypeResponseDto, fval:string) => !!d.name && d.name.toLowerCase().includes(fval.trim().toLowerCase())}
                sx={{ width: 280 }}
                TextFieldProps={{ placeholder: 'Search by Name' }}
              />
              <FilterField 
                label="Status" 
                filterOptions={statusOptions} 
                sourceKey="isEnabled"
                match={(x, filterVal) => {
                  if (filterVal.value == null) { return true }
                  const dto = x as DataInfoTypeResponseDto
                  return dto.isEnabled === filterVal.value
                }}
                sx={{ width: 140, ml: 'auto' }}
              />
              <FilterField
                label="Built-in or not"
                filterOptions={builtInTypeOptions}
                sourceKey="isBuiltIn"
                match={(x, filterVal) => {
                  if (filterVal.value == null) { return true }
                  const dto = x as DataInfoTypeResponseDto
                  return dto.isBuiltIn === filterVal.value
                }}
                sx={{ width: 140 }}
              />
            </AtopFuncArea>
            <Divider sx={{ pb: 0 }} />
            <Scrollbar>
              <Box sx={{ minWidth: 1150 }}>
                <ShowColumCtx.Provider value={states}>
                  <Datagrid
                    hasBulkActions={false} 
                    sx={{
                      '& th' : { py: 1, textTransform: 'uppercase' },
                      '& td' : { py: 2 },
                      '& span.MuiTypography-root.filename' : { maxWidth:400, wordBreak:'break-word' },
                    }}
                    {...listCtx}
                  >
                    <TextField label="Name " source="name" />
                    <TextField label="Display Name" source="displayName" sortable={false} />
                    <FunctionField source="description" sortable={false} 
                      label={
                        <WithShowColumn render={(s) => 
                          <ToggleButtonGroup value={s.showColumn} exclusive={true} size="small" aria-label="description & classification"
                            onChange={(_, showColumn) => setStates(v => ({...v, showColumn}))}
                          >
                            <ToggleButton value="C" key="C">
                              Classification</ToggleButton>
                            <ToggleButton value="D" key="D">
                              Description</ToggleButton>
                          </ToggleButtonGroup>
                        } />
                      }
                      render={(d0?:RecPatch) => {
                        const d = d0 as DataInfoTypeResponseDto
                        if (states.showColumn === 'D') { return (<Typography variant="body2" component="span">{d.description}</Typography>) }

                        const clss = (classifications as SecondaryClassificationResponseDto[])
                          .find(c => c.secondaryClassificationId === d.defaultSecondClassification)
                        const eRule = rules.find(r => r.id === d.defaultEncryptionRule)
                        return (
                          <Stack direction="row" gap={2} alignItems="center">
                            {clss && <ClassificationLabel variant={clss.primaryClassification!} text={clss.name!} />}
                            {eRule && <Chip label={eRule.name} size="small" variant="outlined" />}
                          </Stack>
                        )
                      }}
                    />
                    {/* <TextField label="Base InfoType" source="baseInfoType" sortable={false} /> */}
                    <TextField label="Is BuiltIn" source="isBuiltIn" sortable={false} />
                    <FunctionField 
                      source="isEnabled" 
                      label="Status" 
                      sortable={false}
                      render={(record?:RecPatch) => 
                        <Chip 
                          label={(record as DataInfoTypeResponseDto&RecPatch)?.isEnabled ? 'Enabled' : 'Disabled'}
                          color={((record as DataInfoTypeResponseDto&RecPatch)?.isEnabled ? 'success' : 'warning') as any}
                        />
                      }
                    />
                    <FunctionField label="Action" sortable={false} 
                      render={(d0?:RecPatch) => {
                        const d = d0 as DataInfoTypeResponseDto
                        return (
                          <Tooltip title={hasEditPermit ? "Edit" : 'View'}>
                            <IconButton 
                              sx={{ 
                                color: 'text.primary' as any, 
                                '&:hover': { backgroundColor: 'action.hover' },
                              }}
                              component={RouterLink}
                              to={`/settings/encryption/info-types/${d.id as any}/show`}
                            >
                              {hasEditPermit 
                              ? <EditIcon fontSize="small" />
                              : <ArrowForwardIcon fontSize="small" />}
                            </IconButton>
                          </Tooltip>
                        )
                      }}
                    />
                  </Datagrid>
                </ShowColumCtx.Provider>
              </Box>
            </Scrollbar>
            {//@ts-ignore 
            <DefaultPagination {...listCtx} />}
          </Card>
    //     </Grid>
    //   </Grid>
    // </Content>
  )
}

export default DataInfoTypes;

const builtInTypeOptions : FilterOption<boolean>[] = [
  { label: "Built-in", value: true },
  { label: "Custom", value: false },
]
const statusOptions : FilterOption<boolean>[] = [
  { label: 'Enabled', value: true },
  { label: 'Disabled', value: false },
]

const ShowColumCtx = createContext({} as any)
const WithShowColumn : FC<{render: (x: {showColumn: 'D'|'C'})=>any}> = ({render}) => {
  const states = useContext(ShowColumCtx) as {showColumn: 'D'|'C'}
  return (render(states))
}